import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MmfYieldSparkline from '@/components/MmfYieldSparkline';
import { BarChart3, ArrowUpRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const median = (xs) => {
  const s = xs.filter((x) => typeof x === 'number').sort((a, b) => a - b);
  if (!s.length) return null;
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

// "vs. family median" — peers from the money-market hub payload; falls back
// to the all-fund median when the family has no other tracked fund.
function usePeerMedian(ticker, family, type) {
  const [peer, setPeer] = useState(null);
  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    axios.get(`${API}/money-market-funds`).then(({ data }) => {
      if (cancelled) return;
      const rows = (data?.rows || []).filter((r) => !r.yield_suspect && r.ticker !== ticker);
      const sameType = type ? rows.filter((r) => r.category === type) : [];
      const famType = family ? sameType.filter((r) => r.family === family) : [];
      const pick = (list, label) => setPeer({ label, value: median(list.map((r) => r.seven_day_yield)), n: list.length });
      if (famType.length) pick(famType, `${family} ${type} median`);
      else if (sameType.length) pick(sameType, `${type} fund median`);
      else setPeer({ label: 'all money funds median', value: data?.stats?.median_yield ?? median(rows.map((r) => r.seven_day_yield)), n: rows.length });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [ticker, family, type]);
  return peer;
}

// Same figures and colour thresholds as the /money-market-fund-yields table.
const fmtPct = (v, d = 2) => (typeof v === 'number' ? `${v.toFixed(d)}%` : null);
const fmtAum = (v) => (typeof v !== 'number' ? null
  : v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B`
  : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M`
  : `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const TYPE_CLS = {
  Municipal: 'bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300',
  Prime: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400',
};

function Tile({ label, testId, children, title, foot }) {
  return (
    <div className="rounded-md bg-white dark:bg-cyan-950/40 border border-cyan-500/40 p-3 min-w-0" title={title}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">{label}</div>
      <div className="font-mono text-xl sm:text-2xl font-bold text-cyan-900 dark:text-cyan-100 tabular-nums truncate" data-testid={testId}>
        {children}
      </div>
      {foot}
    </div>
  );
}

function PeerLine({ yield7, peer }) {
  if (typeof yield7 !== 'number' || !peer || typeof peer.value !== 'number') return null;
  const bp = Math.round((yield7 - peer.value) * 100);
  const cls = bp > 0 ? 'text-emerald-600 dark:text-emerald-400' : bp < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400';
  return (
    <div className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400 whitespace-normal" data-testid="mmf-seo-peer-median">
      <span className={`font-semibold tabular-nums ${cls}`}>{bp > 0 ? '+' : ''}{bp} bp</span>
      {' '}vs. {peer.label} {peer.value.toFixed(2)}%
    </div>
  );
}

export default function MmfSeoMetrics({ stock }) {
  const yield7 = fmtPct(stock?.mmf_7day_yield);
  const aum = fmtAum(stock?.mmf_aum);
  const family = stock?.mmf_family_label || stock?.mmf_family || null;
  const type = stock?.mmf_category || stock?.mmf_type || null;
  const peer = usePeerMedian(stock?.ticker, family, type);
  const expense = stock?.mmf_expense_ratio;
  const liquid = stock?.mmf_weekly_liquid_pct;
  const asOf = stock?.mmf_as_of || stock?.mmf_report_date || stock?.mmf_yield_as_of || null;
  const hasAny = yield7 || aum || family || type || typeof expense === 'number' || typeof liquid === 'number';
  if (!hasAny) return null;

  const expenseCls = typeof expense !== 'number' ? ''
    : expense <= 0.2 ? '!text-emerald-600 dark:!text-emerald-400'
    : expense >= 0.5 ? '!text-rose-600 dark:!text-rose-400' : '';
  const liquidCls = typeof liquid !== 'number' ? ''
    : liquid >= 90 ? '!text-emerald-600 dark:!text-emerald-400'
    : liquid <= 55 ? '!text-amber-600 dark:!text-amber-400' : '';

  return (
    <div className="not-prose space-y-2" data-testid="mmf-seo-metrics">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {yield7 && (
          <Tile label="7-Day Yield" testId="mmf-seo-yield" foot={<PeerLine yield7={stock.mmf_7day_yield} peer={peer} />}>
            <span className="text-emerald-600 dark:text-emerald-400">{yield7}</span>
          </Tile>
        )}
        {aum && <Tile label="Total Net Assets" testId="mmf-seo-aum">{aum}</Tile>}
        {family && (
          <Tile label="Family" testId="mmf-seo-family">
            <span className="text-base sm:text-lg font-sans">{family}</span>
          </Tile>
        )}
        {type && (
          <Tile label="Type" testId="mmf-seo-type">
            <span className={`inline-block align-middle px-2 py-0.5 rounded-md text-xs font-bold uppercase tracking-wide font-sans ${TYPE_CLS[type] || 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
              {type}
            </span>
          </Tile>
        )}
        {typeof expense === 'number' && (
          <Tile label="Expense" testId="mmf-seo-expense" title="Net expense ratio from the fund's latest SEC N-CSR shareholder report">
            <span className={expenseCls}>{fmtPct(expense)}</span>
          </Tile>
        )}
        {typeof liquid === 'number' && (
          <Tile label="Weekly Liquid" testId="mmf-seo-weekly-liquid" title="Assets convertible to cash within five business days. SEC Rule 2a-7 sets a 50% floor.">
            <span className={liquidCls}>{fmtPct(liquid, 0)}</span>
          </Tile>
        )}
      </div>
      <MmfYieldSparkline ticker={stock?.ticker} testId="mmf-seo-yield-sparkline" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        {asOf ? (
          <div className="text-[11px] text-cyan-700 dark:text-cyan-400 font-medium" data-testid="mmf-seo-as-of">
            SEC filing as of {new Date(`${String(asOf).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        ) : <span />}
        <Link
          to="/money-market-fund-yields"
          className="inline-flex items-center gap-1.5 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold px-3 py-1.5 transition-colors no-underline"
          data-testid="mmf-seo-hub-link"
        >
          <BarChart3 className="w-3.5 h-3.5" />
          Compare all money market funds
          <ArrowUpRight className="w-3 h-3 opacity-80" />
        </Link>
      </div>
    </div>
  );
}
